import React, { useState, useEffect } from 'react';
import { getCommentsForQuestion } from '../services/questionsService';

/**
 * CommentLeaderboard component displays the top comments for a question as a leaderboard
 * with customizable styling and behavior
 *
 * @param {Object} props
 * @param {string} props.questionId - ID of the question to display leaderboard for
 * @param {Object} props.styles - Customization options for the leaderboard UI
 * @param {string} props.styles.backgroundColor - Background color of leaderboard items
 * @param {string} props.styles.color - Text color of leaderboard items
 * @param {string} props.styles.borderColor - Border color of leaderboard items
 * @param {string} props.styles.borderRadius - Border radius of leaderboard items
 * @param {string} props.styles.boxShadow - Box shadow for leaderboard items
 * @param {string} props.styles.padding - Padding for leaderboard items
 * @param {string} props.styles.fontSize - Font size for leaderboard text
 * @param {string} props.styles.fontWeight - Font weight for leaderboard text
 * @param {string} props.styles.hoverBackgroundColor - Background color on hover
 * @param {string} props.styles.activeBackgroundColor - Background color when active
 * @param {boolean} props.showTomatoCount - Whether to show tomato counts
 * @param {boolean} props.showClapCount - Whether to show clap counts
 * @param {number} props.limit - How many comments to show (default: 3)
 * @param {string} props.sortBy - What to sort by: 'tomatoes', 'claps', or 'ratio' (default: 'tomatoes')
 * @param {function} props.onCommentClick - Callback when a comment is clicked
 */
function CommentLeaderboard({ 
  questionId, 
  styles = {}, 
  showTomatoCount = true,
  showClapCount = false,
  limit = 3,
  sortBy = 'tomatoes',
  onCommentClick
}) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Merge provided styles with default styles
  const defaultStyles = {
    backgroundColor: '#ffffff',
    color: '#000000',
    borderColor: '#e0e0e0',
    borderRadius: '50px',
    padding: '12px 24px',
    margin: '8px 0',
    fontSize: '16px',
    fontWeight: '500',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '600px',
    hoverBackgroundColor: '#f5f5f5',
    activeBackgroundColor: '#e0e0e0',
    itemSpacing: '12px'
  };
  
  const mergedStyles = { ...defaultStyles, ...styles };
  
  useEffect(() => {
    const fetchComments = async () => {
      console.log('CommentLeaderboard: Starting fetchComments for questionId:', questionId);
      setLoading(true);
      setError(null);

      // Early validation
      if (!questionId) {
        console.log('CommentLeaderboard: No questionId provided, showing empty state');
        setComments([]);
        setLoading(false);
        return;
      }

      try {
        console.log('CommentLeaderboard: Fetching comments for question:', questionId);
        
        // Step 1: Check localStorage first
        let result = null;
        let localCommentsFound = false;
        
        console.log('CommentLeaderboard: Checking localStorage...');
        try {
          const localComments = JSON.parse(localStorage.getItem('comments') || '{}');
          console.log('CommentLeaderboard: All localStorage comments:', Object.keys(localComments));
          
          const questionComments = localComments[questionId] || [];
          console.log(`CommentLeaderboard: Found ${questionComments.length} comments for question ${questionId}`);
          
          if (questionComments.length > 0) {
            localCommentsFound = true;
            console.log('CommentLeaderboard: Sample comment:', questionComments[0]);
            
            // Convert localStorage format to expected format
            const formattedComments = questionComments.map((comment, index) => ({
              id: comment.id || `local_comment_${index}`,
              text: comment.text || '',
              userId: comment.userId || 'anonymous',
              userName: comment.userName || 'User',
              tomatoCount: comment.tomatoCount || 0,
              clapCount: comment.clapCount || 0,
              tomatoUsers: comment.tomatoUsers || [],
              clapUsers: comment.clapUsers || [],
              createdAt: comment.createdAt || comment.localCreatedAt || new Date().toISOString()
            }));
            
            result = {
              comments: formattedComments,
              hasMore: false,
              page: 1
            };
            
            console.log('CommentLeaderboard: Formatted localStorage comments:', result);
          }
        } catch (localError) {
          console.warn('CommentLeaderboard: localStorage parsing failed:', localError);
        }
        
        // Step 2: Try Firebase if no local comments found
        if (!localCommentsFound) {
          console.log('CommentLeaderboard: No local comments, trying Firebase...');
          try {
            const firebaseResult = await getCommentsForQuestion(questionId, 1, 100);
            console.log('CommentLeaderboard: Firebase result:', firebaseResult);
            
            if (firebaseResult && firebaseResult.comments && firebaseResult.comments.length > 0) {
              result = firebaseResult;
              console.log('CommentLeaderboard: Using Firebase comments');
            } else {
              console.log('CommentLeaderboard: No comments found in Firebase either');
              
              // Step 3: For testing, create some sample data if this looks like a basketball question
              const basketballKeywords = ['basketball', 'goat', 'g.o.a.t', 'player', 'nba'];
              const isBasketballQuestion = basketballKeywords.some(keyword => 
                questionId.toLowerCase().includes(keyword)
              );
              
              if (isBasketballQuestion) {
                console.log('CommentLeaderboard: Creating sample basketball data for testing');
                const sampleComments = [
                  { 
                    id: 'sample1', 
                    text: 'Michael Jordan', 
                    userId: 'user1', 
                    userName: 'User1', 
                    clapCount: 5, 
                    tomatoCount: 1,
                    createdAt: new Date().toISOString()
                  },
                  { 
                    id: 'sample2', 
                    text: 'LeBron James', 
                    userId: 'user2', 
                    userName: 'User2', 
                    clapCount: 4, 
                    tomatoCount: 2,
                    createdAt: new Date().toISOString()
                  },
                  { 
                    id: 'sample3', 
                    text: 'Michael Jordan', 
                    userId: 'user3', 
                    userName: 'User3', 
                    clapCount: 3, 
                    tomatoCount: 0,
                    createdAt: new Date().toISOString()
                  },
                  { 
                    id: 'sample4', 
                    text: 'Kobe Bryant', 
                    userId: 'user4', 
                    userName: 'User4', 
                    clapCount: 2, 
                    tomatoCount: 1,
                    createdAt: new Date().toISOString()
                  }
                ];
                
                result = {
                  comments: sampleComments,
                  hasMore: false,
                  page: 1
                };
                
                console.log('CommentLeaderboard: Using sample data for testing');
              } else {
                result = { comments: [], hasMore: false, page: 1 };
              }
            }
          } catch (firebaseError) {
            console.error('CommentLeaderboard: Firebase error:', firebaseError);
            result = { comments: [], hasMore: false, page: 1 };
          }
        }

        // Step 4: Process the results
        console.log('CommentLeaderboard: Processing result:', result);
        
        if (result && result.comments) {
          console.log(`CommentLeaderboard: Processing ${result.comments.length} comments for leaderboard`);
          
          // If we have comments, process them
          if (result.comments.length > 0) {

          // Track unique users per answer text (normalized to lowercase)
          const answerCounts = {};
          const answerComments = {};

          // Get list of banned/inappropriate words to filter
          // Get the question data to check for custom banned words
          const questionData = questionId ?
            JSON.parse(localStorage.getItem('cachedQuestions') || '{}')[questionId] || {} : {};

          // Get custom banned words from question settings
          const customBannedWords = questionData.leaderboardOptions?.bannedWords || '';

          // Parse custom banned words from comma-separated string
          const customBannedWordsList = customBannedWords
            .split(',')
            .map(word => word.trim().toLowerCase())
            .filter(word => word.length > 0);

          console.log('Using question-specific banned words:', customBannedWordsList.length > 0 ?
                      customBannedWordsList : 'None provided');

          // Get global banned words from localStorage
          const globalBannedWords = localStorage.getItem('globalBannedWords') || '';

          // Parse global banned words from comma-separated string
          const globalBannedWordsList = globalBannedWords
            .split(',')
            .map(word => word.trim().toLowerCase())
            .filter(word => word.length > 0);

          console.log('Using global banned words:', globalBannedWordsList.length > 0 ?
                      globalBannedWordsList : 'None provided');

          // Default banned words list
          const defaultBannedWords = [
            // Standard profanity
            "fuck", "f*ck", "fuk", "fvck", "f**k", "fuq", "fk",
            "shit", "sh*t", "sh!t", "s**t", "poop",
            "ass", "a$$", "azz", "a**",
            "bitch", "b*tch", "b!tch", "b**ch",
            "dick", "d*ck", "d!ck", "d**k",
            "pussy", "p*ssy", "pu$$y", "p***y",
            "cunt", "c*nt", "c**t",

            // Sexual terms
            "porn", "p0rn", "pr0n", "pron", "p*rn",
            "sex", "s*x", "s3x", "seggs",
            "nude", "n00d", "nud3", "naked",
            "boobs", "boob", "b00b", "tit", "tits", "t!ts",
            "penis", "p3nis", "cock", "c0ck",
            "vagina", "vag", "v@g", "vag!na",

            // Hate speech
            "nazi", "n@zi", "n@z1", "nz",
            "nigger", "n1gger", "n1gg3r", "n1gga", "n1gg@", "nigga", "n!gga",
            "faggot", "f@ggot", "f@gg0t", "f@g", "fag", "f@g",
            "retard", "r3tard", "ret@rd", "tard",
            "chink", "ch1nk", "ch!nk",
            "spic", "sp!c", "sp1c",
            "kike", "k!ke", "k1ke",
            "tranny", "tr@nny", "tr@nn1",

            // Violence
            "rape", "r@pe", "r@p3", "r*pe",
            "kill", "k!ll", "k1ll", "murder",
            "suicide", "su1c1de", "su!c!de",
            "terrorist", "terr0rist", "terr0r1st",

            // Drugs
            "cocaine", "c0caine", "coke", "c0ke",
            "heroin", "her0in", "h3roin",
            "meth", "m3th", "crack",

            // Other inappropriate
            "hitler", "h1tler", "h!tler",
            "osama", "0sama", "bin laden",
            "isis", "1s1s", "terrorist",
            "pedophile", "ped0", "p3do",
          ];

          // Combine default, global, and question-specific banned words
          const bannedWords = [...defaultBannedWords, ...globalBannedWordsList, ...customBannedWordsList];

          // Basketball player whitelist - common and famous players
          // This helps ensure answers are actual basketball players for basketball-related questions
          const basketballPlayers = [
            // All-time greats
            "michael jordan", "jordan", "mj", "lebron james", "lebron", "king james", "kobe bryant", "kobe", "kareem abdul-jabbar",
            "magic johnson", "larry bird", "wilt chamberlain", "bill russell",
            "shaquille o'neal", "hakeem olajuwon", "tim duncan", "kevin durant",
            "stephen curry", "steph curry", "steph", "charles barkley", "dirk nowitzki", "karl malone",
            "kevin garnett", "oscar robertson", "jerry west", "isiah thomas",
            "allen iverson", "patrick ewing", "david robinson", "dwyane wade", "dwade",
            "elgin baylor", "scottie pippen", "john stockton", "chris paul",
            "james harden", "russell westbrook", "giannis antetokounmpo", "giannis",
            "kawhi leonard", "anthony davis", "nikola jokic", "joel embiid",
            "luka doncic", "jayson tatum", "donovan mitchell", "ja morant",
            "kyrie irving", "damian lillard", "devin booker", "paul george",
            "carmelo anthony", "melo", "vince carter", "pau gasol", "tracy mcgrady",
            "ray allen", "reggie miller", "dennis rodman", "dominique wilkins",
            "clyde drexler", "gary payton", "jason kidd", "kd", "cp3",
            "paul pierce", "derrick rose", "yao ming", "dikembe mutombo",

            // More recent stars (last few years)
            "anthony edwards", "ant", "ant-man", "jimmy butler", "bam adebayo", "zion williamson",
            "trae young", "lamelo ball", "jalen brunson", "chet holmgren",
            "victor wembanyama", "wemby", "paolo banchero", "tyler herro", "deandre ayton",

            // WNBA stars
            "a'ja wilson", "aja wilson", "breanna stewart", "diana taurasi",
            "sue bird", "candace parker", "maya moore", "lisa leslie",
            "sheryl swoopes", "elena delle donne", "brittney griner",

            // Add any other relevant basketball players
          ];

          // TEMPORARY: Disable banned words filtering for debugging
          console.log('CommentLeaderboard: Banned words filtering disabled for debugging');
          const bannedWordsRegex = /^$/; // Empty regex that never matches
          const leetSpeakPatterns = []; // Empty array

          // Get the question text to determine domain-specific filtering
          let questionText = '';
          try {
            questionText = questionId ? localStorage.getItem(`question_text_${questionId}`) || '' : '';
            console.log('Question text:', questionText);
          } catch (err) {
            console.warn('Error getting question text:', err);
            questionText = '';
          }

          // Detect question type/domain for context-aware filtering
          const questionDomains = {
            basketball: {
              keywords: /basketball|nba|player|hoops|dunk|court|mvp|finals|playoff/i,
              allowedAnswers: basketballPlayers
            },
            football: {
              keywords: /football|nfl|quarterback|qb|touchdown|receiver|linebacker|super bowl/i,
              allowedAnswers: [
                "tom brady", "patrick mahomes", "peyton manning", "aaron rodgers", "jerry rice",
                "lawrence taylor", "barry sanders", "walter payton", "emmitt smith",
                "ladainian tomlinson", "calvin johnson", "megatron", "dan marino", "joe montana",
                "brett favre", "reggie white", "john elway", "troy aikman", "terry bradshaw",
                "randy moss", "ray lewis", "deion sanders", "primetime", "jim brown",
                "derrick henry", "josh allen", "lamar jackson", "justin herbert", "jalen hurts"
              ]
            },
            music: {
              keywords: /singer|rapper|artist|band|song|music|album|pop|rock|hip hop|rap/i,
              allowedAnswers: [
                "beyonce", "taylor swift", "drake", "michael jackson", "the beatles", "ariana grande",
                "ed sheeran", "rihanna", "adele", "justin bieber", "dua lipa", "weeknd", "the weekend",
                "billie eilish", "kendrick lamar", "jay z", "kanye", "ye", "eminem", "slim shady",
                "lady gaga", "bruno mars", "queen", "elvis", "madonna", "bts", "bad bunny",
                "nirvana", "coldplay", "mariah carey", "prince", "whitney houston", "tupac", "2pac",
                "notorious b.i.g.", "biggie smalls", "david bowie", "bob dylan", "led zeppelin"
              ]
            },
            actors: {
              keywords: /actor|actress|movie star|hollywood|film|movie|tv show|series|netflix/i,
              allowedAnswers: [
                "tom hanks", "leonardo dicaprio", "leo", "meryl streep", "denzel washington",
                "brad pitt", "jennifer lawrence", "robert downey jr", "rdj", "iron man", "tony stark",
                "scarlett johansson", "samuel l jackson", "johnny depp", "morgan freeman",
                "tom cruise", "will smith", "harrison ford", "ryan reynolds", "chris hemsworth",
                "chris evans", "dwayne johnson", "the rock", "emma stone", "viola davis",
                "chadwick boseman", "daniel day-lewis", "anthony hopkins", "kate winslet",
                "jennifer aniston", "sandra bullock", "angelina jolie", "keanu reeves"
              ]
            },
            food: {
              keywords: /food|meal|dish|cuisine|recipe|restaurant|eat|cook|chef|pizza|burger|taco|fries/i,
              allowedAnswers: [
                "pizza", "burger", "pasta", "sushi", "taco", "fried chicken", "steak", "ramen",
                "ice cream", "chocolate", "french fries", "fries", "burrito", "curry", "pho",
                "mac and cheese", "sandwich", "caesar salad", "wings", "lasagna", "lobster",
                "crab", "salmon", "mcdonald's", "in n out", "in-n-out", "chipotle", "chick fil a",
                "popeyes", "kfc", "subway", "taco bell", "wendy's", "burger king", "shake shack"
              ]
            }
          };

          // Determine which domains the question matches
          const matchedDomains = {};
          
          console.log(`CommentLeaderboard: Checking question text for domain matching: "${questionText}"`);

          try {
            Object.keys(questionDomains).forEach(domain => {
              try {
                const regex = questionDomains[domain].keywords;
                const matches = regex.test(questionText.toLowerCase());
                console.log(`CommentLeaderboard: Domain "${domain}" test result:`, matches);
                if (matches) {
                  matchedDomains[domain] = true;
                  console.log(`Question matches ${domain} domain`);
                }
              } catch (regexError) {
                console.warn(`Error testing ${domain} domain:`, regexError);
              }
            });
          } catch (domainError) {
            console.warn('Error in domain matching:', domainError);
          }
          
          console.log(`CommentLeaderboard: Matched domains:`, Object.keys(matchedDomains));

          // First pass: group comments by normalized text and count unique users
          result.comments.forEach(comment => {
            try {
              // Normalize comment text (lowercase, trim)
              const normalizedText = (comment.text || '').toLowerCase().trim();

            // Skip empty comments
            if (!normalizedText) return;

            // Skip extremely short answers (likely not meaningful)
            if (normalizedText.length < 2) return;

            // Skip answers with banned/inappropriate words
            if (bannedWordsRegex.test(normalizedText)) {
              // Check if it's a default banned word or custom banned word for better logging
              const matchedDefaultWord = defaultBannedWords.some(word =>
                new RegExp('\\b' + word + '\\b', 'i').test(normalizedText)
              );

              const matchedGlobalWord = globalBannedWordsList.some(word =>
                new RegExp('\\b' + word + '\\b', 'i').test(normalizedText)
              );

              const matchedCustomWord = customBannedWordsList.some(word =>
                new RegExp('\\b' + word + '\\b', 'i').test(normalizedText)
              );

              if (matchedCustomWord) {
                console.log('Filtering answer by question-specific banned word:', normalizedText);
              } else if (matchedGlobalWord) {
                console.log('Filtering answer by global banned word:', normalizedText);
              } else {
                console.log('Filtering inappropriate answer (default banned word):', normalizedText);
              }
              return;
            }

            // Check against additional leet speak patterns for more sophisticated filtering
            let containsLeetSpeak = false;
            for (const pattern of leetSpeakPatterns) {
              if (pattern.test(normalizedText)) {
                containsLeetSpeak = true;
                break;
              }
            }

            if (containsLeetSpeak) {
              console.log('Filtering inappropriate answer (leet speak):', normalizedText);
              return;
            }

            // Skip likely spam or irrelevant answers (too many special characters)
            if (/^[^a-zA-Z0-9]{3,}$/.test(normalizedText)) {
              console.log('Filtering likely spam:', normalizedText);
              return;
            }

            // Skip answers that are just URLs
            if (/^https?:\/\/\S+$/i.test(normalizedText)) {
              console.log('Filtering URL-only answer:', normalizedText);
              return;
            }

            // TEMPORARY: Skip all filtering for debugging
            console.log(`CommentLeaderboard: Allowing answer "${normalizedText}" (filtering disabled for debugging)`);

            // Initialize answer tracking if this is first time seeing this answer
            if (!answerCounts[normalizedText]) {
              answerCounts[normalizedText] = new Set();
              // Store the first comment with this text
              answerComments[normalizedText] = comment;
            }

            // Add this user to the set of users who gave this answer
            if (comment.userId) {
              answerCounts[normalizedText].add(comment.userId);
            }
            } catch (commentError) {
              console.warn('Error processing comment:', comment, commentError);
            }
          });

          // Create processed comments with unique user counts
          const processedComments = Object.keys(answerCounts).map(text => {
            const baseComment = answerComments[text];
            return {
              ...baseComment,
              // Store count of unique users who gave this answer
              uniqueUserCount: answerCounts[text].size,
              // Keep clap and tomato counts for backward compatibility
              clapCount: baseComment.clapCount || 0,
              tomatoCount: baseComment.tomatoCount || 0
            };
          });

          // Filter comments - require at least 1 user for an answer to be relevant
          // This helps ensure answers appear even with single responses for testing
          const validComments = processedComments.filter(comment => {
            // Minimum threshold - at least 1 user must have given this answer
            const hasMinimumUsers = comment.uniqueUserCount >= 1;

            // Answers should be of reasonable length (not too short, not excessively long)
            const hasReasonableLength = comment.text.length >= 2 && comment.text.length <= 100;

            return hasMinimumUsers && hasReasonableLength;
          });

          // Sort comments based on unique user count (primary) then by claps (secondary)
          const sortedComments = validComments.sort((a, b) => {
            // First sort by unique user count
            if (b.uniqueUserCount !== a.uniqueUserCount) {
              return b.uniqueUserCount - a.uniqueUserCount;
            }
            // If tied, use claps as secondary sort
            return b.clapCount - a.clapCount;
          });

          // Limit to the specified number of comments (or fewer if not enough exist)
          const topComments = sortedComments.slice(0, Math.min(limit, sortedComments.length));
          console.log(`Using ${topComments.length} top comments for leaderboard display`);
          console.log('Unique user counts by answer:',
            topComments.map(c => ({ text: c.text, uniqueUsers: c.uniqueUserCount }))
          );

          setComments(topComments);
          } else {
            // No comments found but this is not an error
            console.log('CommentLeaderboard: No comments found for question:', questionId);
            setComments([]);
          }
        } else {
          // Result is null or invalid
          console.log('CommentLeaderboard: Invalid result, showing empty state');
          setComments([]);
        }
      } catch (err) {
        console.error('Error fetching comments for leaderboard:', err);
        console.error('Error details:', err.message, err.stack);
        
        // Additional debug info
        try {
          const debugComments = JSON.parse(localStorage.getItem('comments') || '{}');
          console.log('Debug - All localStorage comments:', debugComments);
          console.log('Debug - Question ID:', questionId);
          console.log('Debug - Comments for this question:', debugComments[questionId]);
          
          // Try to use local comments as fallback even if there's an error
          if (debugComments[questionId] && Array.isArray(debugComments[questionId]) && debugComments[questionId].length > 0) {
            console.log('Using localStorage comments as fallback due to error');
            const localComments = debugComments[questionId];
            
            // Process local comments the same way we do in the main flow
            const answerCounts = {};
            const answerComments = {};
            
            localComments.forEach(comment => {
              const normalizedText = comment.text.toLowerCase().trim();
              if (!normalizedText || normalizedText.length < 2) return;
              
              if (!answerCounts[normalizedText]) {
                answerCounts[normalizedText] = new Set();
                answerComments[normalizedText] = comment;
              }
              
              if (comment.userId) {
                answerCounts[normalizedText].add(comment.userId);
              }
            });
            
            const processedComments = Object.keys(answerCounts).map(text => {
              const baseComment = answerComments[text];
              return {
                ...baseComment,
                uniqueUserCount: answerCounts[text].size,
                clapCount: baseComment.clapCount || 0,
                tomatoCount: baseComment.tomatoCount || 0
              };
            });
            
            const validComments = processedComments.filter(comment => 
              comment.uniqueUserCount >= 1 && 
              comment.text.length >= 2 && 
              comment.text.length <= 100
            );
            
            const sortedComments = validComments.sort((a, b) => {
              if (b.uniqueUserCount !== a.uniqueUserCount) {
                return b.uniqueUserCount - a.uniqueUserCount;
              }
              return b.clapCount - a.clapCount;
            });
            
            const topComments = sortedComments.slice(0, Math.min(limit, sortedComments.length));
            console.log(`Using ${topComments.length} fallback comments from localStorage`);
            
            setComments(topComments);
            setError(null);
            setLoading(false);
            return;
          }
        } catch (debugErr) {
          console.error('Debug error:', debugErr);
        }
        
        setError('Failed to load comments. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (questionId) {
      fetchComments();
    } else {
      console.log('CommentLeaderboard: No questionId provided, showing sample data');
      setLoading(false);
      
      // For testing, provide mock leaderboard data when no questionId
      setComments([
        {
          id: 'mock1',
          text: 'Michael Jordan',
          uniqueUserCount: 3,
          tomatoCount: 1,
          clapCount: 5
        },
        {
          id: 'mock2',
          text: 'LeBron James',
          uniqueUserCount: 2,
          tomatoCount: 2,
          clapCount: 4
        },
        {
          id: 'mock3',
          text: 'Kobe Bryant',
          uniqueUserCount: 1,
          tomatoCount: 0,
          clapCount: 3
        }
      ]);
    }
  }, [questionId, limit]);
  
  if (loading) {
    return <div className="comment-leaderboard-loading">Loading top comments...</div>;
  }
  
  if (error) {
    return <div className="comment-leaderboard-error">{error}</div>;
  }
  
  if (comments.length === 0) {
    return (
      <div className="comment-leaderboard-empty" style={{ textAlign: 'center', padding: '20px' }}>
        <p style={{ marginBottom: '10px' }}>
          No popular answers yet! Be the first to contribute.
        </p>
        <p style={{ fontSize: '14px', color: '#666' }}>
          Add some answers to see the leaderboard!
        </p>
      </div>
    );
  }
  
  return (
    <div 
      className="comment-leaderboard"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: mergedStyles.itemSpacing,
        width: '100%',
        maxWidth: mergedStyles.maxWidth,
        margin: '0 auto'
      }}
    >
      {comments.map((comment, index) => (
        <button
          key={comment.id}
          className="leaderboard-item"
          onClick={() => onCommentClick && onCommentClick(comment)}
          style={{
            backgroundColor: mergedStyles.backgroundColor,
            color: mergedStyles.color,
            border: `1px solid ${mergedStyles.borderColor}`,
            borderRadius: mergedStyles.borderRadius,
            padding: mergedStyles.padding,
            margin: mergedStyles.margin,
            fontSize: mergedStyles.fontSize,
            fontWeight: mergedStyles.fontWeight,
            boxShadow: mergedStyles.boxShadow,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = mergedStyles.hoverBackgroundColor;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = mergedStyles.backgroundColor;
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.backgroundColor = mergedStyles.activeBackgroundColor;
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.backgroundColor = mergedStyles.hoverBackgroundColor;
          }}
        >
          {/* Rank number indicator */}
          <div 
            className="leaderboard-rank"
            style={{
              position: 'absolute',
              top: '-10px',
              left: '10px',
              backgroundColor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32',
              color: '#000',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            {index + 1}
          </div>
          
          {/* Comment text - truncated if too long */}
          <div 
            className="leaderboard-text"
            style={{
              flex: 1,
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              paddingLeft: '8px', 
              paddingRight: '8px'
            }}
          >
            {comment.text}
          </div>
          
          {/* Stats container */}
          <div 
            className="leaderboard-stats"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            {/* Always show clap count - represents number of users who said this answer */}
            <div className="clap-count" style={{ display: 'flex', alignItems: 'center' }}>
              <span role="img" aria-label="clap" style={{ marginRight: '4px' }}>👏</span>
              <span>{comment.uniqueUserCount || comment.clapCount || 0}</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

export default CommentLeaderboard;